import { Menu } from 'antd';
import CloseIcon from 'components/MainNav/CloseIcon.svg';
import HamburgerMenu from 'components/MainNav/HamburgerMenu.svg';
import KamereoLogo from 'components/MainNav/Kamereo.svg';
import mainNavMessages from 'components/MainNav/messages';
import GlobalIcon from 'components/MainNav/NavIcons/GlobalIcon.svg';
import {
  MenuItemLink,
  MenuLabel,
  ProfileInfoContainer,
  MenuItem as ProfileMenuItem,
  TopNavMenuItem,
} from 'components/MainNav/TopNavComponents';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { setOpenSigninPopup, signout } from 'containers/MainLayout/actions';
import { selectUser, selectStore } from 'containers/MainLayout/selectors';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import styled, { css } from 'styles/styled-components';
import translations from 'translations';
import { commonDimensions } from 'utils/constants';
import media from 'utils/mediaStyle';
import accountMessages from 'utils/messages';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import { useHeaderTheme } from './HeaderThemeContext';
import messages from './messages';
import SolutionsDropdown, {
  useBusinessTypes,
  useFeaturedProducts,
  BUSINESS_TYPE_HREF,
} from './SolutionsDropdown/SolutionsDropdown';
import solutionsDropdownMessages from './SolutionsDropdown/SolutionsDropdown.messages';
import iconArrowUpRight from './SolutionsDropdown/icon-arrow-up-right.svg';
import downloadIcon from './download-icon.svg';
import SimpleNavDropdown, { SimpleNavDropdownItem } from './SimpleNavDropdown';
import renderWithWebDomain from 'utils/renderWithWebDomain';

const ArrowDownIcon: FC<{ $dark?: boolean }> = ({ $dark }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <g clip-path="url(#clip0_11893_10680)">
      <path
        d="M3.9599 5.89063C3.85536 5.89061 3.75401 5.85161 3.68255 5.78125L0.427669 2.56934C0.417546 2.55934 0.412305 2.54536 0.41302 2.53125C0.413867 2.517 0.42121 2.50299 0.432552 2.49414L0.914973 2.11816C0.930028 2.10646 0.950062 2.10413 0.966731 2.11133L0.982356 2.12207L3.9599 5.05957L6.93743 2.12207C6.95098 2.1088 6.97087 2.10419 6.98822 2.10938L7.00482 2.11816L7.48724 2.49414C7.49858 2.50299 7.50592 2.517 7.50677 2.53125C7.50749 2.54536 7.50224 2.55934 7.49212 2.56934L4.23724 5.78125C4.16577 5.85159 4.06442 5.89062 3.9599 5.89063Z"
        fill={$dark ? '#ffffff' : '#4B5563'}
      />
    </g>
    <defs>
      <clipPath id="clip0_11893_10680">
        <rect width="8" height="8" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const MailIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2.33334 3.5H11.6667C12.2833 3.5 12.8333 4.05 12.8333 4.66667V9.33333C12.8333 9.95 12.2833 10.5 11.6667 10.5H2.33334C1.71667 10.5 1.16667 9.95 1.16667 9.33333V4.66667C1.16667 4.05 1.71667 3.5 2.33334 3.5Z"
      stroke="#3c9041"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.8333 4.66667L7 8.16667L1.16667 4.66667"
      stroke="#3c9041"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PhoneIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M12.8333 9.91V11.41C12.834 11.549 12.8055 11.687 12.7497 11.8145C12.6939 11.942 12.6121 12.0563 12.5095 12.15C12.4069 12.2436 12.2858 12.3145 12.154 12.358C12.0222 12.4015 11.8827 12.4167 11.7447 12.4025C10.1955 12.2345 8.70753 11.7113 7.39917 10.8742C6.18215 10.1122 5.14783 9.07783 4.38584 7.86083C3.5458 6.54658 3.02251 5.05144 2.85751 3.49583C2.84334 3.35817 2.85834 3.21908 2.90154 3.08758C2.94474 2.95609 3.01523 2.83513 3.10841 2.73258C3.2016 2.63003 3.31544 2.5482 3.44242 2.49233C3.5694 2.43646 3.70673 2.40779 3.84584 2.4075H5.34584C5.58938 2.40514 5.82601 2.48908 6.01251 2.64416C6.19902 2.79925 6.32334 3.01541 6.36334 3.2525C6.4383 3.72547 6.5808 4.18645 6.78667 4.6275C6.85356 4.77123 6.86962 4.9316 6.83261 5.08442C6.7956 5.23724 6.7075 5.37373 6.58251 5.4725L5.85084 6.05417C6.5615 7.27983 7.58816 8.30649 8.81384 9.01717L9.39551 8.2855C9.49428 8.16051 9.63077 8.07241 9.78359 8.0354C9.93641 7.99839 10.0968 8.01444 10.2405 8.08133C10.6815 8.2872 11.1425 8.4297 11.6155 8.50467C11.8548 8.54492 12.0731 8.67082 12.2286 8.85945C12.384 9.04807 12.4665 9.28691 12.4622 9.53167L12.8333 9.91Z"
      stroke="#3c9041"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const userSelector = selectUser();
const storeSelector = selectStore();

const profileMenu = (onSignout: () => void) => (
  <Menu>
    <Menu.Item key="account">
      <MenuItemLink to="/account-info">
        <MenuLabel>{translations(accountMessages.accountSetting)}</MenuLabel>
      </MenuItemLink>
    </Menu.Item>
    <Menu.Item key="referral" onClick={() => sendGA4Event(EventName.REFERRAL_PROGRAM_CLICK_HUB)}>
      <MenuItemLink to="/refer-friends">
        <MenuLabel>{translations(mainNavMessages.referralMenuItem)}</MenuLabel>
      </MenuItemLink>
    </Menu.Item>
    <Menu.Item key="orders">
      <MenuItemLink to="/orders">
        <MenuLabel>{translations(accountMessages.orderHistory)}</MenuLabel>
      </MenuItemLink>
    </Menu.Item>
    <Menu.Item key="overview">
      <MenuItemLink to="/overview">
        <MenuLabel>{translations(accountMessages.overviewReport)}</MenuLabel>
      </MenuItemLink>
    </Menu.Item>
    <Menu.Item key="company">
      <MenuItemLink to="/company-info">
        <MenuLabel>{translations(accountMessages.businessSettings)}</MenuLabel>
      </MenuItemLink>
    </Menu.Item>
    <Menu.Item key="signout">
      <ProfileMenuItem color="#FE5043" onClick={onSignout}>
        <MenuLabel>{translations(accountMessages.signout)}</MenuLabel>
      </ProfileMenuItem>
    </Menu.Item>
  </Menu>
);

const useMenuItems = () => [
  {
    id: 'shopNow',
    label: translations(messages.navShopNow),
    href: renderWithWebDomain(process.env.APP_ENV, '/'),
    external: true,
  },
  // { id: 'solutions', label: translations(messages.navSolutions), hasChevron: true },
  {
    id: 'whyKamereo',
    label: translations(messages.navWhyKamereo),
    href: '/tai-sao-chon-kamereo',
    external: false,
  },
  { id: 'resources', label: translations(messages.navResources), hasChevron: true },
  { id: 'company', label: translations(messages.navCompany), hasChevron: true },
];

const useResourcesDropdownItems = (lang: string): SimpleNavDropdownItem[] => {
  const isEn = lang === 'en';
  return [
    {
      key: 'businessTips',
      label: translations(messages.footerBusinessTips),
      href: isEn ? 'https://kamereo.vn/blog/en/business-tips/' : 'https://kamereo.vn/blog/vi/kien-thuc-kinh-doanh/',
      external: true,
    },
    {
      key: 'foodLifestyle',
      label: translations(messages.footerFoodLifestyle),
      href: isEn ? 'https://kamereo.vn/blog/en/food-and-lifestyle/' : 'https://kamereo.vn/blog/vi/am-thuc-doi-song/',
      external: true,
    },
    {
      key: 'topPlaces',
      label: translations(messages.footerTopPlaces),
      href: isEn
        ? 'https://kamereo.vn/blog/en/place-to-eat-drink-entertain/'
        : 'https://kamereo.vn/blog/vi/dia-diem-an-uong-vui-choi/',
      external: true,
    },
  ];
};

const useCompanyDropdownItems = (lang: string): SimpleNavDropdownItem[] => {
  const isEn = lang === 'en';
  return [
    {
      key: 'aboutCompany',
      label: translations(messages.companyItem),
      href:
        process.env.APP_ENV === 'production'
          ? 'https://tuyendung.kamereo.vn/ve-cong-ty'
          : 'https://tuyendung.qa.kamereo.vn/ve-cong-ty',
      external: true,
    },
    {
      key: 'news',
      label: translations(messages.footerNews),
      href: isEn ? 'https://kamereo.vn/blog/en/kamereo-news/' : 'https://kamereo.vn/blog/vi/tin-tuc-kamereo/',
      external: true,
    },
    {
      key: 'careers',
      label: translations(messages.footerCareers),
      href: process.env.APP_ENV === 'production' ? 'https://tuyendung.kamereo.vn' : 'https://tuyendung.qa.kamereo.vn',
      external: true,
    },
    {
      key: 'coreValues',
      label: translations(messages.footerCoreValues),
      href: isEn ? 'https://kamereo.vn/core-values' : 'https://kamereo.vn/gia-tri-cot-loi',
      external: true,
    },
    {
      key: 'certifications',
      label: translations(messages.footerCertifications),
      href: isEn ? 'https://kamereo.vn/blog/en/certifications/' : 'https://kamereo.vn/blog/vi/chung-nhan/',
      external: true,
    },
    {
      key: 'press',
      label: translations(messages.footerPress),
      href: isEn
        ? 'https://kamereo.vn/blog/en/kamereo-in-the-newspaper/'
        : 'https://kamereo.vn/blog/vi/bao-chi-noi-gi-ve-kamereo/',
      external: true,
    },
  ];
};

const DESKTOP_NAV_BREAKPOINT_PX = 1250;

const showAboveDesktopNavBreakpoint = css`
  @media (min-width: ${DESKTOP_NAV_BREAKPOINT_PX + 1}px) {
    display: flex;
  }
`;

const hideAboveDesktopNavBreakpoint = css`
  @media (min-width: ${DESKTOP_NAV_BREAKPOINT_PX + 1}px) {
    display: none;
  }
`;

const HeaderWrapper = styled.header<{ $dark?: boolean; $mobileMenuOpen?: boolean }>`
  position: fixed;
  top: 0;
  z-index: ${({ $mobileMenuOpen }) => ($mobileMenuOpen ? 1100 : 1000)};
  width: 100%;
  background: ${({ $dark }) => ($dark ? '#000000' : '#ffffff')};
  box-shadow: ${({ $dark }) =>
    $dark ? '0px 1px 0px 0px rgba(255, 255, 255, 0.08)' : '0px 1px 0px 0px rgba(0, 0, 0, 0.08)'};
  transition:
    background-color 0.4s ease,
    box-shadow 0.4s ease;
`;

const WIDE_CONTAINER_PATH = '/tai-sao-chon-kamereo';
const WIDE_CONTAINER_BREAKPOINT_PX = 1600;
const WIDE_CONTAINER_MAX_WIDTH_PX = 1568;

const HeaderInner = styled.div<{ $useWideContainer?: boolean }>`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 14px 16px;
  ${media.lg`
    padding: 12px 64px;
  `}
  ${({ $useWideContainer }) =>
    $useWideContainer &&
    `
    @media (min-width: ${WIDE_CONTAINER_BREAKPOINT_PX + 1}px) {
      max-width: ${WIDE_CONTAINER_MAX_WIDTH_PX}px;
    }
  `}
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  & > div {
    width: 140px;
    height: 24px;
  }
  ${media.lg`
    & > div {
      width: 164px;
      height: 28px;
    }
  `}
`;

const NavList = styled.nav`
  display: none;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${showAboveDesktopNavBreakpoint}
`;

const NavItemWrapper = styled.div`
  position: relative;
  display: flex;
`;

const NavItem = styled.a<{ $dark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 8px 16px;
  border-radius: 80px;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: ${({ $dark }) => ($dark ? '#ffffff' : '#111827')};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6')};
  }
`;

const RightSection = styled.div`
  display: none;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  ${showAboveDesktopNavBreakpoint}
`;

const LangSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const GlobalIconWrapper = styled.span<{ $dark?: boolean }>`
  display: flex;
  ${({ $dark }) =>
    $dark &&
    css`
      svg path {
        fill: #ffffff;
      }
    `}
`;

const LangOption = styled.button<{ active?: boolean; $dark?: boolean }>`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: ${({ active, $dark }) => {
    if (active) return '#3c9041';
    return $dark ? '#ffffff' : '#111827';
  }};
`;

const LangDivider = styled.span<{ $dark?: boolean }>`
  width: 1px;
  height: 14px;
  background: ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.24)' : '#bfc3cb')};
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SigninButton = styled.button<{ $dark?: boolean }>`
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 10px 16px;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ $dark }) => ($dark ? '#ffffff' : '#111827')};
  &:hover {
    background: ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6')};
    color: ${({ $dark }) => ($dark ? '#ffffff' : '#111827')};
  }
`;

const SignupButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 10px 16px;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  background: #43a047;
  color: #ffffff;
  &:hover {
    background: #388e3c;
    color: #ffffff;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 8px;
  margin-left: auto;
  ${hideAboveDesktopNavBreakpoint}
`;

const MobileLangSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin-right: -40px;

  margin-left: auto;
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  top: 60px;
  background: #ffffff;
  z-index: 99;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  ${hideAboveDesktopNavBreakpoint}
`;

const MobileGuestBanner = styled.div`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(111, 195, 115, 0.16) 0%, rgba(255, 209, 128, 0.12) 100%);
`;

const MobileProfileBanner = styled.div`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(111, 195, 115, 0.16) 0%, rgba(255, 209, 128, 0.12) 100%);
`;

const MobileBannerText = styled.p`
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #111827;
  opacity: 0.9;
`;

const MobileBannerButtonRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  gap: 12px;
`;

const MobileBannerSignupButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 10px 16px;
  border-radius: 100px;
  background: #43a047;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  &:hover {
    color: #ffffff;
    opacity: 0.9;
  }
`;

const MobileBannerSigninButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 10px 16px;
  border: 1px solid #43a047;
  border-radius: 100px;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #3c9041;
  cursor: pointer;
`;

const MobileAvatar = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #f25b50;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const MobileBusinessName = styled.p`
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileLogoutButton = styled.button`
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 10px 16px;
  border: 1px solid #43a047;
  border-radius: 100px;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #3c9041;
  cursor: pointer;
`;

const MobileNavList = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 4px 0;
`;

const MobileShopNowItem = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 500;
  color: #111827;
`;

const MobileNavItem = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 500;
  color: #111827;
`;

const MobileAccordionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  cursor: pointer;
  text-align: left;
`;

const MobileAccordionChevron = styled.span<{ $open?: boolean }>`
  display: flex;
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const MobileAccordionBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px 12px;
`;

const MobileSectionLabel = styled.p`
  margin: 0;
  padding: 16px 0 8px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
`;

const MobileSubLink = styled.a`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 8px 16px;
  font-size: 14px;
  color: #111827;
`;

const MobileContactBlock = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #f2f3f5;
`;

const MobileContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileContactText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #3c9041;
`;

const MobileAppBanner = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #f2f3f5;
  margin-top: auto;
`;

const MobileAppBannerText = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 12px;
  color: #4b5563;
`;

const MobileAppBannerButton = styled.a`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`;

const setLangAndReload = (newLang: 'vi' | 'en') => {
  window.localStorage.setItem('lang', newLang);
  const url = new URL(window.location.href);
  if (newLang === 'en') {
    url.searchParams.set('lang', 'en');
  } else {
    url.searchParams.delete('lang');
  }
  window.location.href = url.toString();
};

const Header: FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [openMobileAccordionId, setOpenMobileAccordionId] = useState<string | null>(null);
  const { isDarkMode } = useHeaderTheme();
  const location = useLocation();
  const isWideContainerPath = location.pathname === WIDE_CONTAINER_PATH;
  const lang = window.localStorage.getItem('lang') || 'vi';
  const menuItems = useMenuItems();
  const resourcesDropdownItems = useResourcesDropdownItems(lang);
  const companyDropdownItems = useCompanyDropdownItems(lang);
  const businessTypes = useBusinessTypes();
  const featuredProducts = useFeaturedProducts();
  const dispatch = useDispatch();
  const user = useSelector(userSelector) || {};
  const store = useSelector(storeSelector) || {};
  const loggedIn = !!window.localStorage.getItem('token');
  const handleSignout = () => dispatch(signout());
  const handleOpenSigninPopup = () => {
    setIsMobileMenuOpen(false);
    dispatch(setOpenSigninPopup(true));
  };
  const toggleMobileAccordion = (id: string) => setOpenMobileAccordionId((prev) => (prev === id ? null : id));
  const businessName: string = store.name || user.name || '';

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = documentElement.style.overflow;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isMobileMenuOpen]);

  return (
    <HeaderWrapper $dark={isDarkMode} $mobileMenuOpen={isMobileMenuOpen}>
      <HeaderInner $useWideContainer={isWideContainerPath}>
        <LogoLink to="/ve-kamereo" onClick={() => setIsMobileMenuOpen(false)}>
          <SVGIcon src={KamereoLogo} renderAsImg={false} imgAttr={{ alt: 'Kamereo' }} />
        </LogoLink>
        <NavList>
          {menuItems.map((item) =>
            item.hasChevron ? (
              <NavItemWrapper
                key={item.id}
                onMouseEnter={() => setOpenDropdownId(item.id)}
                onMouseLeave={() => setOpenDropdownId((prev) => (prev === item.id ? null : prev))}
              >
                <NavItem $dark={isDarkMode}>
                  {item.label}
                  <ArrowDownIcon $dark={isDarkMode} />
                </NavItem>
                {openDropdownId === item.id && item.id === 'solutions' && <SolutionsDropdown />}
                {openDropdownId === item.id && item.id === 'resources' && (
                  <SimpleNavDropdown items={resourcesDropdownItems} />
                )}
                {openDropdownId === item.id && item.id === 'company' && (
                  <SimpleNavDropdown items={companyDropdownItems} />
                )}
              </NavItemWrapper>
            ) : (
              <NavItem
                $dark={isDarkMode}
                key={item.id}
                href={item.href}
                target={item.href?.startsWith('http') && item.external ? '_blank' : undefined}
              >
                {item.label}
              </NavItem>
            ),
          )}
        </NavList>
        <RightSection>
          <LangSwitcher>
            <GlobalIconWrapper $dark={isDarkMode}>
              <SVGIcon src={GlobalIcon} renderAsImg={false} />
            </GlobalIconWrapper>
            <LangOption $dark={isDarkMode} active={lang === 'vi'} onClick={() => setLangAndReload('vi')}>
              VN
            </LangOption>
            <LangDivider $dark={isDarkMode} />
            <LangOption $dark={isDarkMode} active={lang === 'en'} onClick={() => setLangAndReload('en')}>
              EN
            </LangOption>
          </LangSwitcher>
          {loggedIn ? (
            <TopNavMenuItem overlay={() => profileMenu(handleSignout)} trigger={['click']} placement="bottomRight">
              <ProfileInfoContainer user={user} />
            </TopNavMenuItem>
          ) : (
            <AuthButtons>
              <SigninButton $dark={isDarkMode} type="button" onClick={handleOpenSigninPopup}>
                {translations(messages.signin)}
              </SigninButton>
              <SignupButton to="/contact-us">{translations(messages.signup)}</SignupButton>
            </AuthButtons>
          )}
        </RightSection>
        {isMobileMenuOpen && (
          <MobileLangSwitcher>
            <SVGIcon src={GlobalIcon} renderAsImg={false} />
            <LangOption
              active={lang === 'vi'}
              onClick={() => {
                setLangAndReload('vi');
                setIsMobileMenuOpen(false);
              }}
            >
              VN
            </LangOption>
            <LangDivider />
            <LangOption
              active={lang === 'en'}
              onClick={() => {
                setLangAndReload('en');
                setIsMobileMenuOpen(false);
              }}
            >
              EN
            </LangOption>
          </MobileLangSwitcher>
        )}
        <MobileMenuButton onClick={() => setIsMobileMenuOpen((prev) => !prev)}>
          <SVGIcon src={isMobileMenuOpen ? CloseIcon : HamburgerMenu} renderAsImg={false} />
        </MobileMenuButton>
      </HeaderInner>
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenuOverlay
            id="mobile-nav-overlay"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            {loggedIn ? (
              <MobileProfileBanner>
                <MobileAvatar>{(businessName || 'K').charAt(0).toUpperCase()}</MobileAvatar>
                <MobileBusinessName>{businessName}</MobileBusinessName>
                <MobileLogoutButton type="button" onClick={handleSignout}>
                  {translations(accountMessages.signout)}
                </MobileLogoutButton>
              </MobileProfileBanner>
            ) : (
              <MobileGuestBanner>
                <MobileBannerText>{translations(messages.mobileLoginBannerText)}</MobileBannerText>
                <MobileBannerButtonRow>
                  <MobileBannerSignupButton to="/contact-us">{translations(messages.signup)}</MobileBannerSignupButton>
                  <MobileBannerSigninButton type="button" onClick={handleOpenSigninPopup}>
                    {translations(messages.signin)}
                  </MobileBannerSigninButton>
                </MobileBannerButtonRow>
              </MobileGuestBanner>
            )}

            <MobileNavList>
              <MobileShopNowItem href="/">
                {translations(messages.navShopNow)}
                <SVGIcon src={iconArrowUpRight} renderAsImg={false} />
              </MobileShopNowItem>

              {/* <MobileAccordionHeader type="button" onClick={() => toggleMobileAccordion('solutions')}>
                {translations(messages.navSolutions)}
                <MobileAccordionChevron $open={openMobileAccordionId === 'solutions'}>
                  <ArrowDownIcon />
                </MobileAccordionChevron>
              </MobileAccordionHeader>
              {openMobileAccordionId === 'solutions' && (
                <MobileAccordionBody>
                  <MobileSectionLabel>
                    {translations(solutionsDropdownMessages.sectionBusinessTypes)}
                  </MobileSectionLabel>
                  {businessTypes.map((item) => (
                    <MobileSubLink key={item.key} as={Link} to={BUSINESS_TYPE_HREF}>
                      {item.title}
                    </MobileSubLink>
                  ))}
                  <MobileSectionLabel>
                    {translations(solutionsDropdownMessages.sectionFeaturedProducts)}
                  </MobileSectionLabel>
                  {featuredProducts.map((product) => (
                    <MobileSubLink key={product.key} as={Link} to={product.href}>
                      {product.title}
                    </MobileSubLink>
                  ))}
                </MobileAccordionBody>
              )} */}

              <MobileNavItem href={renderWithWebDomain(process.env.APP_ENV, '/tai-sao-chon-kamereo')} target="_blank">
                {translations(messages.navWhyKamereo)}
              </MobileNavItem>

              <MobileAccordionHeader type="button" onClick={() => toggleMobileAccordion('resources')}>
                {translations(messages.navResources)}
                <MobileAccordionChevron $open={openMobileAccordionId === 'resources'}>
                  <ArrowDownIcon />
                </MobileAccordionChevron>
              </MobileAccordionHeader>
              {openMobileAccordionId === 'resources' && (
                <MobileAccordionBody>
                  {resourcesDropdownItems.map((item) => (
                    <MobileSubLink key={item.key} href={item.href} target="_blank" rel="noopener noreferrer">
                      {item.label}
                    </MobileSubLink>
                  ))}
                </MobileAccordionBody>
              )}

              <MobileAccordionHeader type="button" onClick={() => toggleMobileAccordion('company')}>
                {translations(messages.navCompany)}
                <MobileAccordionChevron $open={openMobileAccordionId === 'company'}>
                  <ArrowDownIcon />
                </MobileAccordionChevron>
              </MobileAccordionHeader>
              {openMobileAccordionId === 'company' && (
                <MobileAccordionBody>
                  {companyDropdownItems.map((item) => (
                    <MobileSubLink key={item.key} href={item.href} target="_blank" rel="noopener noreferrer">
                      {item.label}
                    </MobileSubLink>
                  ))}
                </MobileAccordionBody>
              )}
            </MobileNavList>

            <MobileContactBlock>
              <MobileContactRow>
                <MailIcon />
                <MobileContactText>{translations(messages.contactEmailValue)}</MobileContactText>
              </MobileContactRow>
              <MobileContactRow>
                <PhoneIcon />
                <MobileContactText>{translations(messages.contactPhoneValue)}</MobileContactText>
              </MobileContactRow>
            </MobileContactBlock>

            {!loggedIn && (
              <MobileAppBanner>
                <MobileAppBannerText>{translations(messages.mobileAppBannerText)}</MobileAppBannerText>
                <MobileAppBannerButton
                  href="https://play.google.com/store/apps/details?id=vn.kamereo.buyer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {translations(messages.mobileAppBannerCta)}
                  <SVGIcon src={downloadIcon} renderAsImg={false} />
                </MobileAppBannerButton>
              </MobileAppBanner>
            )}
          </MobileMenuOverlay>
        )}
      </AnimatePresence>
    </HeaderWrapper>
  );
};

export default Header;
